import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, timeout, delay } from 'rxjs/operators';
import { RestfulApiService } from '../restful-api.service';
import {
  CreateCommentRequest,
  AnswerCommentRequest,
  CommentResponse,
  CommentsListResponse,
} from '../../models/project/comment.model';
import { MOCK_COMMENTS_API_RESPONSE } from '../../mocks/comment-mock.data';

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  private useMockData = false;

  constructor(private api: RestfulApiService) {}

  /**
   * Enable mock data mode for development/testing
   */
  enableMockData(): void {
    this.useMockData = true;
    console.log('[CommentService] Mock data mode enabled');
  }

  /**
   * Disable mock data mode
   */
  disableMockData(): void {
    this.useMockData = false;
    console.log('[CommentService] Mock data mode disabled');
  }

  /**
   * Create or update a comment/question for a project
   * @param projectUuid - Project UUID
   * @param commentData - Comment data (content, anonymous, entityUuid, commentType)
   * @returns Observable<CommentResponse>
   */
  createOrUpdateComment(
    projectUuid: string,
    commentData: CreateCommentRequest
  ): Observable<CommentResponse> {
    const params = {
      projectUuid: projectUuid,
    };

    return this.api.put(
      `/v1/comment/create-update`,
      commentData,
      params
    ).pipe(
      map((response: any) => ({
        success: true,
        data: response.data || response,
        message: response.message || 'Comment submitted successfully',
      })),
      catchError((error) => {
        console.error('[CommentService] Error creating comment:', error);
        throw {
          success: false,
          message: error.error?.message || 'Failed to submit comment',
        };
      })
    );
  }

  /**
   * Get comments for a project
   * @param projectUuid - Project UUID
   * @param commentType - Type of comment ('QUESTION' or 'COMMENT')
   * @returns Observable<CommentsListResponse>
   */
  getComments(
    projectUuid: string,
    commentType: 'QUESTION' | 'COMMENT' = 'QUESTION'
  ): Observable<CommentsListResponse> {
    const params = {
      projectUuid: projectUuid,
      commentType: commentType,
    };

    return this.api.get(
      `/v1/comment/list`,
      params
    ).pipe(
      map((response: any) => ({
        success: true,
        data: response.data || response || [],
        message: response.message || 'Comments retrieved successfully',
      })),
      catchError((error) => {
        console.error('[CommentService] Error fetching comments:', error);
        throw {
          success: false,
          message: error.error?.message || 'Failed to fetch comments',
        };
      })
    );
  }

  /**
   * Delete a comment
   * @param commentUuid - Comment UUID
   * @returns Observable<CommentResponse>
   */
  deleteComment(commentUuid: string): Observable<CommentResponse> {
    return this.api.delete(
      `/v1/comment/${commentUuid}`
    ).pipe(
      map((response: any) => ({
        success: true,
        data: response.data || response,
        message: response.message || 'Comment deleted successfully',
      })),
      catchError((error) => {
        console.error('[CommentService] Error deleting comment:', error);
        throw {
          success: false,
          message: error.error?.message || 'Failed to delete comment',
        };
      })
    );
  }

  /**
   * Delete a question via the delete-comment endpoint
   * @param commentUuid - Comment UUID to delete
   * @returns Observable<CommentResponse>
   */
  deleteQuestion(commentUuid: string): Observable<CommentResponse> {
    return this.api.delete(
      `/v1/comment/delete-comment`,
      { commentUuid }
    ).pipe(
      map((response: any) => ({
        success: true,
        data: response.data || response,
        message: response.message || 'Question deleted successfully',
      })),
      catchError((error) => {
        console.error('[CommentService] Error deleting question:', error);
        throw {
          success: false,
          message: error.error?.message || 'Failed to delete question',
        };
      })
    );
  }

  /**
   * Submit an answer to a question
   * @param projectUuid - Project UUID
   * @param questionUuid - The UUID of the question being answered
   * @param content - Answer content
   * @returns Observable<CommentResponse>
   */
  submitAnswer(
    projectUuid: string,
    questionUuid: string,
    content: string
  ): Observable<CommentResponse> {
    const payload: AnswerCommentRequest = {
      questionUuid,
      commentType: 'ANSWER',
      content,
    };

    return this.api.put(
      `/v1/comment/create-update`,
      payload,
      { projectUuid }
    ).pipe(
      map((response: any) => ({
        success: true,
        data: response.data || response,
        message: response.message || 'Answer submitted successfully',
      })),
      catchError((error) => {
        console.error('[CommentService] Error submitting answer:', error);
        throw {
          success: false,
          message: error.error?.message || 'Failed to submit answer',
        };
      })
    );
  }

  /**
   * Get list of comments for a project
   * This endpoint is used specifically for the questions tab
   * @param projectUuid - Project UUID
   * @returns Observable<CommentsListResponse>
   */
  getCommentsList(projectUuid: string): Observable<CommentsListResponse> {
    // Return mock data if enabled
    if (this.useMockData) {
      console.log('[CommentService] Returning mock comments data');
      return of(MOCK_COMMENTS_API_RESPONSE).pipe(
        delay(500), // Simulate network delay
        map((response: any) => {
          console.log('[CommentService] Mock API response:', response);

          // Use the same parsing logic as real API
          let flattenedComments: any[] = [];

          if (response && response.response && Array.isArray(response.response)) {
            response.response.forEach((userGroup: any) => {
              if (userGroup.userQuestions && Array.isArray(userGroup.userQuestions)) {
                const mappedQuestions = userGroup.userQuestions.map((q: any) => ({
                  uuid: q.uuid,
                  content: q.content,
                  anonymous: q.anonymous,
                  entityUuid: q.entityUuid,
                  commentType: q.commentType,
                  createdAt: q.updateTime,
                  updatedAt: q.updateTime,
                  user: q.userInfo ? {
                    uuid: q.userInfo.userUuid,
                    name: q.userInfo.firstName && q.userInfo.lastName
                      ? `${q.userInfo.firstName} ${q.userInfo.lastName}`.trim()
                      : (q.userInfo.firstName || q.userInfo.lastName || 'Anonymous'),
                    photoUrl: q.userInfo.profileImageUrl,
                  } : null,
                  replies: q.answer ? [{
                    uuid: q.answer.uuid,
                    content: q.answer.content,
                    anonymous: false,
                    entityUuid: q.entityUuid,
                    commentType: 'COMMENT',
                    createdAt: q.answer.updateTime,
                    user: q.answer.userInfo ? {
                      uuid: q.answer.userInfo.userUuid,
                      name: q.answer.userInfo.firstName && q.answer.userInfo.lastName
                        ? `${q.answer.userInfo.firstName} ${q.answer.userInfo.lastName}`.trim()
                        : 'Project Manager',
                      photoUrl: q.answer.userInfo.profileImageUrl,
                    } : null
                  }] : []
                }));

                flattenedComments = flattenedComments.concat(mappedQuestions);
              }
            });
          }

          console.log('[CommentService] Flattened mock comments:', flattenedComments);

          return {
            success: true,
            data: flattenedComments,
            message: 'Mock comments retrieved successfully',
          };
        })
      );
    }

    const params = {
      projectUuid: projectUuid,
    };

    console.log('[CommentService] Calling GET /v1/comment/list/comments with params:', params);

    return this.api.get(
      `/v1/comment/list/comments`,
      params
    ).pipe(
      timeout(30000), // 30 second timeout
      map((response: any) => {
        console.log('[CommentService] Raw API response:', response);

        // Extract and flatten userQuestions from the response
        let flattenedComments: any[] = [];

        if (response && response.response && Array.isArray(response.response)) {
          // Response structure: { response: [ { userQuestions: [...] }, ... ] }
          response.response.forEach((userGroup: any) => {
            if (userGroup.userQuestions && Array.isArray(userGroup.userQuestions)) {
              // Map API fields to our Comment model
              const mappedQuestions = userGroup.userQuestions.map((q: any) => ({
                uuid: q.uuid,
                content: q.content,
                anonymous: q.anonymous,
                entityUuid: q.entityUuid,
                commentType: q.commentType,
                createdAt: q.updateTime, // Map updateTime to createdAt
                updatedAt: q.updateTime,
                user: q.userInfo ? {
                  uuid: q.userInfo.userUuid,
                  name: q.userInfo.firstName && q.userInfo.lastName
                    ? `${q.userInfo.firstName} ${q.userInfo.lastName}`.trim()
                    : (q.userInfo.firstName || q.userInfo.lastName || 'Anonymous'),
                  photoUrl: q.userInfo.profileImageUrl,
                } : null,
                replies: q.answer ? [{
                  uuid: q.answer.uuid,
                  content: q.answer.content,
                  anonymous: false,
                  entityUuid: q.entityUuid,
                  commentType: 'COMMENT',
                  createdAt: q.answer.updateTime,
                  user: q.answer.userInfo ? {
                    uuid: q.answer.userInfo.userUuid,
                    name: q.answer.userInfo.firstName && q.answer.userInfo.lastName
                      ? `${q.answer.userInfo.firstName} ${q.answer.userInfo.lastName}`.trim()
                      : 'Project Manager',
                    photoUrl: q.answer.userInfo.profileImageUrl,
                  } : null
                }] : []
              }));

              flattenedComments = flattenedComments.concat(mappedQuestions);
            }
          });
        }

        console.log('[CommentService] Flattened and mapped comments:', flattenedComments);

        return {
          success: true,
          data: flattenedComments,
          message: 'Comments retrieved successfully',
        };
      }),
      catchError((error) => {
        console.error('[CommentService] Error fetching comments list:', error);
        console.error('[CommentService] Error details:', error.error);

        // Return an observable with error response instead of throwing
        return of({
          success: false,
          data: [],
          message: error.name === 'TimeoutError'
            ? 'Request timed out. Please try again.'
            : (error.error?.message || 'Failed to fetch comments'),
        });
      })
    );
  }
}
